<div class="w-full h-full flex flex-col">
  <!-- Error message -->
  @if (error) {
    <div
      class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-ds-md mb-4">
      <p class="font-medium">Error loading documents</p>
      <p class="text-sm">{{ error.message }}</p>
    </div>
  }

  @if (!error) {
    <!-- Sticky toolbar -->
    <div class="relative flex-none z-20">
      <div
        class="toolbar sticky top-0 flex flex-wrap items-center gap-2 min-h-12 px-2 py-1.5 bg-ds-surface border border-ds-border rounded-ds-md">
        <!-- Segmented status control (replaces mat-tab-group; same onTabChange/tabCountLabel) -->
        <div role="tablist" aria-label="Document status"
             class="flex items-center gap-0.5 p-0.5 bg-ds-surface-muted rounded-ds-md">
          @for (s of STATUSES; track s; let i = $index) {
            <button type="button"
                    role="tab"
                    [attr.data-testid]="'ocr-history-tab-' + s"
                    [attr.id]="'ocr-history-tab-btn-' + s"
                    [attr.aria-selected]="activeTab === s"
                    (click)="onTabChange(i)"
                    class="seg-pill inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium capitalize rounded-ds-sm cursor-pointer text-ds-text-muted hover:text-ds-text transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
                    [class.seg-pill-active]="activeTab === s">
              {{ s }}
              <span
                class="font-data text-[10px] leading-none px-1 py-0.5 rounded-ds-sm bg-ds-border/60 text-ds-text-muted seg-count">{{ tabCountLabel(s) }}</span>
            </button>
          }
        </div>

        <!-- Filename search (always visible) -->
        <div class="relative flex-1 min-w-36 max-w-72">
          <mat-icon aria-hidden="true"
                    class="!text-[16px] !w-4 !h-4 !leading-4 absolute left-2 top-1/2 -translate-y-1/2 text-ds-text-muted pointer-events-none">search</mat-icon>
          <input data-testid="ocr-filters-filename-input"
                 type="text"
                 aria-label="Search filename"
                 placeholder="Search filename..."
                 class="w-full h-8 pl-7 pr-2 text-sm font-data bg-ds-bg text-ds-text placeholder:text-ds-text-muted border border-ds-border rounded-ds-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ds-secondary focus:border-ds-secondary"
                 [value]="filters.filename"
                 (input)="filters.filename = $any($event.target).value; onFiltersChanged()"/>
        </div>

        <!-- Needs-attention chip (amber) -->
        <button type="button"
                data-testid="ocr-filters-needs-attention-checkbox"
                [attr.aria-pressed]="filters.needsAttention"
                (click)="filters.needsAttention = !filters.needsAttention; onFiltersChanged()"
                matTooltip="Show only documents needing review (failed or needs review, and not yet reviewed)"
                class="inline-flex items-center gap-1 h-8 px-2.5 text-xs font-medium rounded-ds-sm border cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
                [class.bg-ds-accent]="filters.needsAttention"
                [class.text-ds-on-accent]="filters.needsAttention"
                [class.border-ds-accent]="filters.needsAttention"
                [class.bg-ds-surface]="!filters.needsAttention"
                [class.text-ds-text-muted]="!filters.needsAttention"
                [class.border-ds-border]="!filters.needsAttention">
          <mat-icon aria-hidden="true" class="!text-[16px] !w-4 !h-4 !leading-4">warning_amber</mat-icon>
          <span class="hidden sm:inline whitespace-nowrap">Needs attention</span>
          <span class="sr-only sm:hidden">Needs attention</span>
        </button>

        <!-- Date-range drawer toggle -->
        <button type="button"
                data-testid="ocr-history-toggle-filters-btn"
                (click)="showFilters = !showFilters"
                [attr.aria-expanded]="showFilters"
                aria-controls="ocr-history-date-drawer"
                class="inline-flex items-center gap-1 h-8 px-2.5 text-xs font-medium rounded-ds-sm border border-ds-border bg-ds-surface text-ds-text-muted hover:text-ds-text hover:bg-ds-surface-muted cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary"
                [class.!text-ds-primary]="showFilters"
                [class.!border-ds-primary]="showFilters">
          <mat-icon aria-hidden="true" class="!text-[16px] !w-4 !h-4 !leading-4">date_range</mat-icon>
          <span class="hidden sm:inline">Dates</span>
          <span class="sr-only sm:hidden">Date filters</span>
        </button>

        <!-- Reset filters, visible only when there are active filters -->
        @if (activeFilterCount() > 0) {
          <button type="button"
                  data-testid="ocr-history-reset-filters-btn"
                  (click)="resetFilters()"
                  class="inline-flex items-center gap-1 text-xs font-medium text-ds-primary hover:underline cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary rounded-ds-sm px-1 h-8">
            Reset
            <span
              class="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full text-[10px] font-semibold font-data bg-ds-primary text-ds-on-primary"
              [title]="activeFilterCount() + ' active filters'">{{ activeFilterCount() }}</span>
          </button>
        }

        <span class="flex-1"></span>

        <!-- Upload (amber CTA, replaces the old FAB) -->
        <button type="button"
                data-testid="ocr-history-upload-btn"
                (click)="uploadClick.emit()"
                aria-label="Upload Document for OCR"
                class="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-ds-sm bg-ds-accent text-ds-on-accent hover:bg-ds-accent-hover cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary focus-visible:ring-offset-1">
          <mat-icon aria-hidden="true" class="!text-[16px] !w-4 !h-4 !leading-4">upload_file</mat-icon>
          <span class="hidden sm:inline">Upload</span>
        </button>
      </div>

      <!-- Date drawer (absolute overlay below toolbar) -->
      @if (showFilters) {
        <div id="ocr-history-date-drawer"
             class="date-drawer absolute left-0 right-0 top-full mt-1 z-30 p-3 bg-ds-surface border border-ds-border rounded-ds-md shadow-lg">
          <app-ocr-history-filters [filters]="filters"
                                   (filtersChange)="onChildFiltersChange($event)"></app-ocr-history-filters>
        </div>
      }
    </div>

    <!-- Content -->
    <div class="flex-1 min-h-0 mt-2"
         role="tabpanel"
         [attr.aria-labelledby]="'ocr-history-tab-btn-' + activeTab">
      @if (isCollapsed && selectedDocument) {
        <app-resizable-container [initialPercent]="20">
          <div left class="h-full pr-2">
            <ng-container [ngTemplateOutlet]="listView"></ng-container>
          </div>
          <div right class="h-full pl-2">
            <ng-container [ngTemplateOutlet]="sideView"></ng-container>
          </div>
        </app-resizable-container>
      } @else {
        <div class="h-full">
          <ng-container [ngTemplateOutlet]="listView"></ng-container>
        </div>
      }
    </div>
  }
</div>


<ng-template #sideView>
  <!-- OCR Results (appears when a document is selected) -->
  <div class="h-full bg-ds-surface border border-ds-border rounded-ds-md flex flex-col">
    <div
      class="flex justify-between items-center px-3 py-2 border-b border-ds-border bg-ds-surface z-10">
      <h3 class="text-sm font-semibold font-data text-ds-text" data-testid="ocr-history-details-title">Document Details</h3>
      <button data-testid="ocr-history-close-details-btn" (click)="closeDetails()"
              aria-label="Close details"
              class="inline-flex items-center justify-center w-8 h-8 rounded-ds-sm cursor-pointer text-ds-text-muted hover:text-ds-text hover:bg-ds-surface-muted transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-secondary">
        <mat-icon aria-hidden="true">close</mat-icon>
      </button>
    </div>

    <!-- OCR Result Component with scrollable content -->
    <div class="flex-1 overflow-auto p-3 thin-scroll">
      <app-ocr-document-viewer-page [isDetached]="false" [documentInput]="selectedDocument"></app-ocr-document-viewer-page>
    </div>
  </div>
</ng-template>

<ng-template #listView>
  <!-- Document list for the active status tab -->
  <div class="h-full flex flex-col min-h-0">
    <!-- Loading indicator -->
    @if (activeTabState.loading) {
      <div class="flex justify-center items-center py-8">
        <mat-progress-spinner diameter="24" mode="indeterminate"></mat-progress-spinner>
        <span class="ml-2 text-sm text-ds-text-muted">Loading documents...</span>
      </div>
    }

    <!-- No documents message -->
    @if (!activeTabState.loading && !activeTabState.loadingMore && !activeTabState.filtered.length && !activeTabState.nextToken) {
      <div class="bg-ds-bg border border-ds-border rounded-ds-md p-12 text-center">
        <mat-icon class="mx-auto text-ds-text-muted mb-4 !text-5xl !w-12 !h-12" aria-hidden="true">
          {{ activeFilterCount() > 0 ? 'search' : 'insert_drive_file' }}
        </mat-icon>
        <p class="text-sm text-ds-text-muted mb-4">
          @if (activeFilterCount() > 0) {
            No documents match your current filters
          } @else {
            No documents found in this category
          }
        </p>
        @if (activeFilterCount() > 0) {
          <button mat-stroked-button (click)="resetFilters()"
                  class="!cursor-pointer !text-ds-primary !border-ds-primary hover:!bg-ds-primary/5">
            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">filter_alt_off</mat-icon>
            Clear all filters
          </button>
        }
      </div>
    }

    <div class="list-pane flex-1 min-h-0 overflow-auto thin-scroll bg-ds-surface border border-ds-border rounded-ds-md"
         [class.hidden]="!activeTabState.filtered.length && !activeTabState.nextToken && !activeTabState.loadingMore">
      <mat-list class="!py-0">
        @for (doc of activeTabState.filtered; track doc.s3Key; let last = $last) {
          <mat-list-item data-testid="ocr-history-document-row" (click)="selectDocument(doc)"
                         class="doc-row cursor-pointer"
                         [class.selected-row]="selectedDocument && selectedDocument.s3Key === doc.s3Key">
            <div class="row-grid w-full items-center gap-x-3">
              <!-- Filename + info -->
              <div class="col-main min-w-0">
                <span class="block truncate text-sm font-medium font-data text-ds-text">
                  {{ doc.originalFileName || 'Unknown' }}
                </span>
                <span class="col-info block truncate text-xs text-ds-text-muted">
                  @if (getExtractedFieldCount(doc); as count) {
                    {{ count }} fields extracted
                  } @else {
                    @switch (doc.status) {
                      @case ('persisted') {
                        <span class="text-ds-warning">Pending processing...</span>
                      }
                      @case ('processing') {
                        <span class="text-ds-info">Processing document...</span>
                      }
                      @case ('error') {
                        <span class="text-ds-danger" [matTooltip]="'Open the document to see the full template selection trace'">{{ getShortErrorMessage(doc) }}</span>
                      }
                    }
                  }
                  @if (doc.webhookDelivery?.state === 'failed') {
                    <span class="text-ds-danger font-medium" data-testid="ocr-history-webhook-failed"> · Webhook failed</span>
                  }
                </span>
              </div>

              <!-- Template -->
              <div class="col-template min-w-0">
                @if (getTemplateName(doc.usedTemplateId); as templateName) {
                  <span
                    class="px-2 py-0.5 inline-flex items-center max-w-full text-xs leading-4 font-medium rounded-full border bg-ds-surface"
                    [style.border-color]="getTemplateColor(doc.usedTemplateId)"
                    [style.color]="getTemplateColor(doc.usedTemplateId)"
                    [matTooltip]="templateName">
                    <span class="truncate">{{ templateName }}</span>
                  </span>
                }
              </div>

              <!-- Relative time -->
              <div class="col-time text-xs font-data text-ds-text-muted whitespace-nowrap text-right"
                   [matTooltip]="formatFullTimestamp(doc.timestamp)" matTooltipShowDelay="10">
                {{ relativeTime(doc.timestamp) }}
              </div>

              <!-- Status badge -->
              <div class="col-badge flex justify-end">
                <span
                  [class.bg-green-100]="doc.status === 'done'"
                  [class.text-green-800]="doc.status === 'done'"
                  [class.dark:bg-green-900]="doc.status === 'done'"
                  [class.dark:text-green-400]="doc.status === 'done'"
                  [class.bg-blue-100]="doc.status === 'processing'"
                  [class.text-blue-800]="doc.status === 'processing'"
                  [class.dark:bg-blue-900]="doc.status === 'processing'"
                  [class.dark:text-blue-400]="doc.status === 'processing'"
                  [class.bg-yellow-100]="doc.status === 'persisted'"
                  [class.text-yellow-800]="doc.status === 'persisted'"
                  [class.dark:bg-yellow-900]="doc.status === 'persisted'"
                  [class.dark:text-yellow-400]="doc.status === 'persisted'"
                  [class.bg-red-100]="doc.status === 'error'"
                  [class.text-red-800]="doc.status === 'error'"
                  [class.dark:bg-red-900]="doc.status === 'error'"
                  [class.dark:text-red-400]="doc.status === 'error'"
                  class="px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full">
                  {{ doc.status }}
                </span>
              </div>

              <!-- Reviewed / judge verdict indicators -->
              <div class="col-indicators flex items-center justify-end gap-1">
                @if (doc.webhookDelivery?.state === 'failed') {
                  <span class="inline-flex items-center justify-center"
                        data-testid="ocr-history-webhook-indicator"
                        [matTooltip]="webhookTooltip(doc.webhookDelivery)"
                        [attr.aria-label]="webhookTooltip(doc.webhookDelivery)">
                    <mat-icon aria-hidden="true"
                              class="!text-[16px] !leading-none !w-4 !h-4 inline-block text-ds-danger">cloud_off</mat-icon>
                  </span>
                }
                @if (doc.reviewed) {
                  <span
                    class="inline-flex items-center justify-center"
                    [matTooltip]="'Reviewed' + (doc.reviewedBy ? ' by ' + doc.reviewedBy : '') + (doc.reviewedAt ? ' on ' + formatFullTimestamp(doc.reviewedAt) : '')"
                    [attr.aria-label]="'Reviewed ' + (doc.reviewedBy || '')">
                    <mat-icon aria-hidden="true"
                              class="!text-[16px] !leading-none !w-4 !h-4 inline-block text-ds-success">thumb_up</mat-icon>
                    <span class="sr-only">Reviewed</span>
                  </span>
                } @else if (doc.ocrJudgeResult?.overallVerdict) {
                  <span
                    class="inline-flex items-center justify-center w-5 h-5 rounded-full text-white"
                    [class.bg-green-600]="doc.ocrJudgeResult?.overallVerdict === 'pass'"
                    [class.bg-yellow-500]="doc.ocrJudgeResult?.overallVerdict === 'needs_review'"
                    [class.bg-red-600]="doc.ocrJudgeResult?.overallVerdict === 'fail'"
                    [matTooltip]="'OCR Judgment: ' + doc.ocrJudgeResult?.overallVerdict + ' (' + (((doc.ocrJudgeResult?.overallConfidence ?? 0) * 100) | number:'1.0-0') + '%)'"
                    [attr.aria-label]="'OCR Judgment: ' + doc.ocrJudgeResult?.overallVerdict">
                    @switch (doc.ocrJudgeResult?.overallVerdict) {
                      @case ('pass') {
                        <mat-icon aria-hidden="true" class="!text-[14px] !leading-none !w-3.5 !h-3.5 inline-block">check</mat-icon>
                      }
                      @case ('needs_review') {
                        <mat-icon aria-hidden="true" class="!text-[14px] !leading-none !w-3.5 !h-3.5 inline-block">priority_high</mat-icon>
                      }
                      @case ('fail') {
                        <mat-icon aria-hidden="true" class="!text-[14px] !leading-none !w-3.5 !h-3.5 inline-block">close</mat-icon>
                      }
                      @default {
                        <mat-icon aria-hidden="true" class="!text-[14px] !leading-none !w-3.5 !h-3.5 inline-block">priority_high</mat-icon>
                      }
                    }
                    <span class="sr-only">{{ doc.ocrJudgeResult?.overallVerdict }}</span>
                  </span>
                }
              </div>
            </div>
          </mat-list-item>
          @if (!last) {
            <mat-divider/>
          }
        }
      </mat-list>
      <!-- Infinite Scroll Sentinel -->
      <div #infiniteScrollSentinel class="flex justify-center py-4 min-h-12">
        @if (activeTabState.nextToken && activeTabState.loadingMore) {
          <div class="flex items-center gap-2">
            <mat-progress-spinner diameter="20" mode="indeterminate"></mat-progress-spinner>
            <span class="text-xs text-ds-text-muted">Loading more documents...</span>
          </div>
        }
      </div>
    </div>
  </div>
</ng-template>
